// I am using this code for both website and app
import type { ModuleDef } from "@edcc/game-data";

const artPath = (file: string) => `/ed-icons/modules/${file}`;
const hardpointPath = (file: string) => `/ed-icons/hardpoints ingame/${file}`;

const hardpointArt: Record<string, string> = {
  "abrasion blaster": "Abrasion_Blaster.webp", "advanced plasma accelerator": "advanced plasma accel.webp",
  "beam laser": "Beam_Laser.webp", "burst laser": "Burst_Laser.webp", "cannon": "cannon.webp",
  "concord cannon": "Concord_Cannon.webp", "cytoscrambler burst laser": "Cytoscrambler.webp",
  "enzyme missile rack": "enzyme.webp", "fragment cannon": "frag cannon.webp",
  "mine launcher": "MineLauncher_Ingame.webp", "mining laser": "mining laser.webp",
  "mining volley repeater": "Mining_Volley_Repeater.webp", "missile rack": "MissileRack_Ingame.webp",
  "multi-cannon": "multicannon.webp", "pack-hound missile rack": "Pack-Hound_Missile_Rack.webp",
  "pacifier frag-cannon": "Pacifier_Frag-Cannons.webp", "plasma accelerator": "Plasma-Accelerator.webp",
  "pulse laser": "Pulse_Laser.webp", "rail gun": "Railgun_Ingame.webp",
  "remote release flak launcher": "Remote_Release_Flak_Launcher.webp",
  "remote release flechette launcher": "Remote_Release_Flechette_Launcher.webp",
  "seeker missile rack": "Seeker_Missile_Rack.webp", "seismic charge launcher": "Seismic_Charge_Launcher.webp",
  "shock cannon": "Shock-Cannon-Elite-Dangerous.webp", "sub-surface displacement missile": "Sub-Surface_Displacement_Missile.webp",
  "torpedo pylon": "TorpedoPylon_Ingame.webp",
};

const namePatterns: Array<[RegExp, string]> = [
  [/experimental weapon stabiliser/, artPath("Experimental_Weapon_Stabiliser.webp")],
  [/abrasion blaster/, artPath("Abrasion_Blaster.webp")],
  [/seismic charge launcher/, artPath("Seismic_Charge_Launcher.webp")],
  [/sub.surface displacement/, artPath("Sub_Surface_Displacement_Missile.webp")],
  [/mining volley repeater/, artPath("Mining_Volley_Repeater.webp")],
  [/mining laser/, artPath("Mining_Laser.webp")],
  [/pulse wave analyser|pulse wave analyzer/, artPath("Pulse_Wave_Analyser.webp")],
  [/heat sink launcher/, artPath("Heat_Sink_Launcher.webp")],
  [/multi.cannon/, artPath("Multi_Cannon.webp")],
  [/beam laser/, artPath("Beam_Laser.webp")],
  [/burst laser/, artPath("Burst_Laser.webp")],
  [/pulse laser/, artPath("Pulse_Laser.webp")],
  [/advanced plasma/, artPath("Advanced_Plasma_Accelerator.webp")],
  [/plasma accelerator/, artPath("Class_4_Plasma_Accelerator.webp")],
  [/pacifier.*(frag|fragment)/, artPath("Pacifier_Fragment_Cannon.webp")],
  [/(frag|fragment) cannon/, artPath("Fragment_Cannon.webp")],
  [/concord cannon/, artPath("Concord_Cannon.webp")],
  [/shock cannon/, artPath("Shock_Cannon.webp")],
  [/cytoscrambler/, artPath("Cytoscrambler.webp")],
  [/enzyme missile/, artPath("Enzyme_Missile_Rack.webp")],
  [/pack.hound/, artPath("Pack_Hound_Missile_Rack.webp")],
  [/seeker missile/, artPath("Seeker_Missile_Rack.webp")],
  [/missile rack/, artPath("Missile_Rack.webp")],
  [/torpedo/, artPath("Torpedo_Pylon.webp")],
  [/mine launcher/, artPath("Mine_Launcher.webp")],
  [/remote release flak/, artPath("Remote_Release_Flak_Launcher.webp")],
  [/remote release flechette/, artPath("Remote_Release_Flechette_Launcher.webp")],
  [/rail.?gun/, artPath("Railgun.webp")],
  [/cannon/, artPath("Cannon.webp")],
  [/thruster/, artPath("Thrusters.webp")],
  [/point defen[cs]e/, artPath("Point_Defence.webp")],
  [/shield booster/, artPath("Shield_Booster.webp")],
  [/hull reinforcement/, artPath("Hull_Reinforcement_Package.webp")],
  [/lightweight alloy|reinforced alloy|military grade composite|mirrored surface composite|reactive surface composite|bulkhead/, artPath("Hull.webp")],
  [/docking computer/, artPath("Standard_Docking_Computer.webp")],
  [/supercruise assist/, artPath("Supercruise_Assist.webp")],
  [/verity/, artPath("Verity.webp")],
  [/frame shift drive interdictor/, artPath("FrameShiftDriveInterdictor_Ingame.webp")],
  [/frame shift drive/, artPath("Frame_Shift_Drive.webp")],
  [/guardian fsd booster/, artPath("Guardian_FSD_Booster_icon.webp")],
  [/power plant/, artPath("Power_Plant.webp")],
  [/power distributor/, artPath("Power_Distributor.webp")],
  [/life support/, artPath("Life_Support.webp")],
  [/sensors?$/, artPath("Sensors.webp")],
  [/fuel tank/, artPath("Fuel_Tank.webp")],
  [/fuel scoop/, artPath("FuelScoop_Ingame.webp")],
  [/cargo rack/, artPath("CargoCapacity_Ingame.webp")],
  [/shield cell bank/, artPath("ShieldCellBank_Ingame.webp")],
  [/shield generator/, artPath("ShieldGenerator_Ingame.webp")],
  [/fighter hangar/, artPath("FighterHangar.webp")],
  [/limpet controller/, artPath("Limpet_Controller_icon.webp")],
  [/planetary vehicle hangar/, artPath("Planetary_Vehicle_Hangar_icon.webp")],
  [/planetary approach suite/, artPath("Planetary-Approach-Suite-Ship.webp")],
  [/refinery/, artPath("Refinery_Ingame.webp")],
  [/auto field/, artPath("AutoFieldMaintenanceUnit_Ingame.webp")],
  [/detailed surface scanner/, artPath("DetailedSurfaceScanner_Ingame.webp")],
  [/advanced discovery scanner/, artPath("Advanced-Discovery-Scanner.webp")],
];

const symbolPatterns: Array<[RegExp, string]> = [
  [/weaponstabiliser|weaponstabilizer/, artPath("Experimental_Weapon_Stabiliser.webp")],
  [/abrasionblaster/, artPath("Abrasion_Blaster.webp")],
  [/seismiccharge/, artPath("Seismic_Charge_Launcher.webp")],
  [/subsurfacedisplacement/, artPath("Sub_Surface_Displacement_Missile.webp")],
  [/miningvolley/, artPath("Mining_Volley_Repeater.webp")],
  [/mininglaser/, artPath("Mining_Laser.webp")],
  [/multicannon/, artPath("Multi_Cannon.webp")],
  [/pulsewaveanalyser|pulsewaveanalyzer/, artPath("Pulse_Wave_Analyser.webp")],
  [/heatsink/, artPath("Heat_Sink_Launcher.webp")],
  [/beamlaser/, artPath("Beam_Laser.webp")],
  [/burstlaser/, artPath("Burst_Laser.webp")],
  [/pulselaser/, artPath("Pulse_Laser.webp")],
  [/advancedplasma/, artPath("Advanced_Plasma_Accelerator.webp")],
  [/plasmaaccelerator/, artPath("Class_4_Plasma_Accelerator.webp")],
  [/pacifier/, artPath("Pacifier_Fragment_Cannon.webp")],
  [/fragcannon|fragmentcannon/, artPath("Fragment_Cannon.webp")],
  [/concordcannon/, artPath("Concord_Cannon.webp")],
  [/shockcannon/, artPath("Shock_Cannon.webp")],
  [/cytoscrambler/, artPath("Cytoscrambler.webp")],
  [/enzymemissile/, artPath("Enzyme_Missile_Rack.webp")],
  [/packhound/, artPath("Pack_Hound_Missile_Rack.webp")],
  [/seekermissile/, artPath("Seeker_Missile_Rack.webp")],
  [/missilerack/, artPath("Missile_Rack.webp")],
  [/torpedo/, artPath("Torpedo_Pylon.webp")],
  [/minelauncher/, artPath("Mine_Launcher.webp")],
  [/remotereleaseflak|flaklauncher/, artPath("Remote_Release_Flak_Launcher.webp")],
  [/remotereleaseflechette|flechette/, artPath("Remote_Release_Flechette_Launcher.webp")],
  [/railgun/, artPath("Railgun.webp")],
  [/hpt_cannon/, artPath("Cannon.webp")],
  [/int_engine|mainengines|thruster/, artPath("Thrusters.webp")],
  [/pointdefence|pointdefense/, artPath("Point_Defence.webp")],
  [/shieldbooster/, artPath("Shield_Booster.webp")],
  [/hullreinforcement/, artPath("Hull_Reinforcement_Package.webp")],
  [/bulkhead|int_armour/, artPath("Hull.webp")],
  [/dockingcomputer/, artPath("Standard_Docking_Computer.webp")],
  [/supercruiseassist/, artPath("Supercruise_Assist.webp")],
  [/verity|voicepack/, artPath("Verity.webp")],
  [/fsdinterdictor/, artPath("FrameShiftDriveInterdictor_Ingame.webp")],
  [/hyperdrive|frameshiftdrive/, artPath("Frame_Shift_Drive.webp")],
  [/guardianfsdbooster/, artPath("Guardian_FSD_Booster_icon.webp")],
  [/powerplant/, artPath("Power_Plant.webp")],
  [/powerdistributor/, artPath("Power_Distributor.webp")],
  [/lifesupport/, artPath("Life_Support.webp")],
  [/sensors|radar/, artPath("Sensors.webp")],
  [/fuel_tank|fueltank/, artPath("Fuel_Tank.webp")],
  [/fuelscoop/, artPath("FuelScoop_Ingame.webp")],
  [/cargorack/, artPath("CargoCapacity_Ingame.webp")],
  [/shieldcellbank/, artPath("ShieldCellBank_Ingame.webp")],
  [/shieldgenerator/, artPath("ShieldGenerator_Ingame.webp")],
  [/fighterbay/, artPath("FighterHangar.webp")],
  [/limpet/, artPath("Limpet_Controller_icon.webp")],
  [/vehiclehangar|planetlandingmodule/, artPath("Planetary_Vehicle_Hangar_icon.webp")],
  [/planetapproachsuite/, artPath("Planetary-Approach-Suite-Ship.webp")],
  [/refinery/, artPath("Refinery_Ingame.webp")],
  [/repairer/, artPath("AutoFieldMaintenanceUnit_Ingame.webp")],
  [/detailsurfacescanner/, artPath("DetailedSurfaceScanner_Ingame.webp")],
  [/advanceddiscoveryscanner|discoveryscanner/, artPath("Advanced-Discovery-Scanner.webp")],
];

export interface ModuleMarkerSource {
  name?: string | null;
  symbol?: string | null;
  group?: string | null;
  mount?: string | null;
  category?: string | null;
  purchaseCurrency?: "credits" | "merc-coin";
  preEngineered?: ModuleDef["preEngineered"];
}

export function moduleArtFor(module: { name?: string | null; symbol?: string | null }): string | null {
  const name = module.name?.trim().toLowerCase() ?? "";
  if (name && hardpointArt[name]) return hardpointPath(hardpointArt[name]);
  if (name) {
    for (const [pattern, src] of namePatterns) {
      if (pattern.test(name)) return src;
    }
  }

  const symbol = module.symbol?.trim().toLowerCase() ?? "";
  if (symbol) {
    for (const [pattern, src] of symbolPatterns) {
      if (pattern.test(symbol)) return src;
    }
  }

  return null;
}

export function isGuardianModule(module: ModuleMarkerSource | null | undefined): boolean {
  if (!module) return false;
  return /guardian/i.test(`${module.name ?? ""} ${module.symbol ?? ""}`) ||
    ["ntp", "gfsb", "ggc", "ghrp", "gmrp", "gpc", "gsc", "gsrp"].includes(module.group ?? "");
}

export function isMercenaryGear(module: ModuleMarkerSource | null | undefined): boolean {
  return module?.category === "mercgear";
}
